import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import readerApi from "../../services/readerApi";
import { getSocket } from "../../services/socket";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import { classifyType } from "../AuditLog/eventTypes";
import "./TagDetails.css";

const ACTIVITY_PAGE_SIZE = 8;

/*
 * TagDetails — single-tag detail view, reached from any place that mentions
 * a tag (dashboard table, logs, notification bell, cashier receipts, scan
 * results). One endpoint (`GET /tags/:tid/details`) returns everything, so
 * page renders fast with no N+1 fetches.
 *
 * Supported edits right here on the page:
 *   - endDate: native date picker + quick offsets (+30d, +6mo, +1y). Saves
 *     via `PUT /tags/:tid { endDate }` → server logs TAG_ENDDATE_SET.
 *   - state: toggle READY / BLOCKED / ON_DELIVERY. Uses the same update
 *     endpoint. Server handles state-machine rules.
 *   - rid (room): a move requires approval → creates a pending transfer.
 *     We surface that in the toast so the operator isn't surprised.
 *
 * Role gating matches the rest of the app: workers+ can edit their scope.
 */

const STATE_STYLE = {
  READY:           { label: "Ready",        bg: "#DCFCE7", color: "#166534" },
  BLOCKED:         { label: "Blocked",      bg: "#FEE2E2", color: "#991B1B" },
  ON_DELIVERY:     { label: "On delivery",  bg: "#DBEAFE", color: "#1E40AF" },
  PENDING_REQUEST: { label: "Pending",      bg: "#FEF3C7", color: "#92400E" },
};

const toDateInput = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "ago" : "from now";
  if (abs < 45 * 1000)        return "just now";
  if (abs < 60 * 60 * 1000)   return `${Math.round(abs / 60000)} min ${sign}`;
  if (abs < 24 * 60 * 60 * 1000) return `${Math.round(abs / 3600000)} h ${sign}`;
  if (abs < 7 * 24 * 60 * 60 * 1000) return `${Math.round(abs / 86400000)} d ${sign}`;
  return d.toLocaleString();
};

const TagDetails = () => {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isManagerOrAdmin, isWorkerOrAbove } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null); // "endDate" | "state" | "rid"
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);

  // Draft edit state — kept locally so saves are explicit.
  const [draftEndDate, setDraftEndDate] = useState("");
  const [draftState, setDraftState] = useState("");
  const [draftRid, setDraftRid] = useState("");

  const canEdit = isWorkerOrAbove();
  const canMoveRoom = isManagerOrAdmin(); // matches existing rfid.controller rules (any worker+ can request, but UX fits manager)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTagDetails(tid);
      setData(res);
      setDraftEndDate(toDateInput(res?.tag?.endDate));
      setDraftState(res?.tag?.state || "BLOCKED");
      setDraftRid(String(res?.tag?.rid ?? 0));
    } catch (err) {
      showToast(err.message || "Failed to load tag", 3000, true, "danger");
      if (err.status === 404) navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [tid, showToast, navigate]);

  useEffect(() => { load(); }, [load]);

  // Live updates: any tags:updated / tags:deleted socket event that
  // matches THIS tag should refresh the page immediately.  Covers:
  //   • Gate detection flipped the state (READY→ON_DELIVERY, check-in
  //     BLOCKED, etc.) from the Python reader backend.
  //   • A transfer was accepted from a manager's UI on another device.
  //   • Someone else edited the end date / state / room.
  // We reload the full detail so the recent-activity list picks up any
  // new audit events that fired with the state change.
  useEffect(() => {
    if (!tid) return undefined;
    let socket;
    try { socket = getSocket(); } catch { return undefined; }
    if (!socket) return undefined;
    const onUpdated = (payload) => {
      if (!payload?.tags?.length) return;
      const hit = payload.tags.some((t) => t?.tid === tid);
      if (hit) load();
    };
    const onDeleted = (payload) => {
      if (!payload?.tids?.length) return;
      if (payload.tids.includes(tid)) {
        // Tag is gone — bounce back to dashboard
        showToast("This tag was deleted", 2500, true, "danger");
        navigate("/dashboard", { replace: true });
      }
    };
    socket.on("tags:updated", onUpdated);
    socket.on("tags:deleted", onDeleted);

    // ---- Python SSE: direct tag-state stream -------------------------
    // Reliable same-machine path that works even when the Node socket
    // chain isn't delivering.  If either channel fires for this TID we
    // reload.
    let tagStream;
    try { tagStream = new EventSource(readerApi.tagsStreamUrl()); } catch { /* ignore */ }
    if (tagStream) {
      tagStream.onmessage = (ev) => {
        if (!ev?.data) return;
        try {
          const payload = JSON.parse(ev.data);
          if (payload?.tag?.tid === tid) load();
        } catch { /* ignore */ }
      };
    }

    return () => {
      socket.off("tags:updated", onUpdated);
      socket.off("tags:deleted", onDeleted);
      try { tagStream?.close(); } catch { /* ignore */ }
    };
  }, [tid, load, navigate, showToast]);

  // Rooms for the move dropdown — load eagerly on mount so the correct room
  // is already selected when the card renders (avoids the "Unassigned" flash).
  useEffect(() => {
    let cancelled = false;
    const fetchRooms = async () => {
      setRoomsLoading(true);
      try {
        const res = await api.getRooms({ limit: 500 });
        if (!cancelled) setRooms(res?.rooms || []);
      } catch {
        /* non-fatal — dropdown still works, just empty */
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    };
    fetchRooms();
    return () => { cancelled = true; };
  }, []);

  const tag = data?.tag;
  const product = data?.product;
  const room = data?.room;
  const warehouse = data?.warehouse;
  const location = data?.location;

  const endDirtyState = useMemo(() => {
    if (!tag) return null;
    const current = toDateInput(tag.endDate);
    return draftEndDate !== current;
  }, [tag, draftEndDate]);

  const stateDirty = tag && draftState !== tag.state;
  const ridDirty   = tag && String(draftRid) !== String(tag.rid ?? 0);

  // ---- actions ----

  const saveEndDate = async () => {
    if (!tag || !endDirtyState) return;
    setSavingField("endDate");
    try {
      const iso = draftEndDate ? new Date(draftEndDate).toISOString() : null;
      await api.setTagEndDate(tag.tid, iso);
      showToast(iso ? "End date saved" : "End date cleared", 2200, true, "success");
      await load();
    } catch (err) {
      showToast(err.message || "Failed to save end date", 3000, true, "danger");
    } finally {
      setSavingField(null);
    }
  };

  const offsetEndDate = (amount, unit) => {
    const base = draftEndDate ? new Date(draftEndDate) : new Date();
    if (unit === "days")   base.setDate(base.getDate() + amount);
    if (unit === "months") base.setMonth(base.getMonth() + amount);
    if (unit === "years")  base.setFullYear(base.getFullYear() + amount);
    setDraftEndDate(toDateInput(base));
  };

  const saveState = async () => {
    if (!tag || !stateDirty) return;
    setSavingField("state");
    const prevState = tag.state;
    const nextState = draftState;
    // Optimistic update — show the new state immediately without waiting for
    // the network round-trip.  If the API call fails we roll back.
    setData((prev) =>
      prev ? { ...prev, tag: { ...prev.tag, state: nextState } } : prev
    );
    try {
      await api.updateTag(tag.tid, { state: nextState });
      showToast("State updated", 2200, true, "success");
      load(); // background sync (no await) — refreshes audit events & timeline
    } catch (err) {
      // Roll back the optimistic update
      setData((prev) =>
        prev ? { ...prev, tag: { ...prev.tag, state: prevState } } : prev
      );
      setDraftState(prevState);
      showToast(err.message || "Failed to update state", 3000, true, "danger");
    } finally {
      setSavingField(null);
    }
  };

  const saveRid = async () => {
    if (!tag || !ridDirty) return;
    setSavingField("rid");
    try {
      const res = await api.updateTag(tag.tid, { rid: Number(draftRid) });
      if (res?.pending) {
        showToast("Transfer request created — awaiting manager approval", 3200, true, "success");
      } else {
        showToast("Room updated", 2200, true, "success");
      }
      await load();
    } catch (err) {
      showToast(err.message || "Failed to change room", 3000, true, "danger");
    } finally {
      setSavingField(null);
    }
  };

  // ---- render ----

  if (loading && !data) {
    return (
      <div className="dashboard-container tag-details-page">
        <div className="dashboard-content">
          <div className="loading-state">
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 32, color: "var(--text-tertiary)" }} />
            <p>Loading tag…</p>
          </div>
        </div>
      </div>
    );
  }
  if (!tag) return null;

  const endDate = tag.endDate ? new Date(tag.endDate) : null;
  const daysRemaining = tag.daysRemaining;
  const endTone = tag.expired ? "expired" : daysRemaining != null && daysRemaining <= 30 ? "soon" : daysRemaining != null ? "ok" : "none";
  const stateStyle = STATE_STYLE[tag.state] || STATE_STYLE.BLOCKED;

  return (
    <div className="dashboard-container tag-details-page">
      <div className="dashboard-content">
        {/* Back + breadcrumb */}
        <div className="td-breadcrumb">
          <button type="button" className="td-back-btn" onClick={() => navigate(-1)} title="Go back">
            <i className="fa fa-arrow-left" aria-hidden /> Back
          </button>
          <span className="td-breadcrumb-sep">·</span>
          <span>Tag</span>
          <span className="td-breadcrumb-sep">/</span>
          <span className="td-breadcrumb-current">{tag.tid}</span>
        </div>

        {/* Hero: product image, name, key badges */}
        <div className="td-hero">
          <div className="td-hero-image">
            {product?.productImgLink ? (
              <img src={product.productImgLink} alt={product.name} />
            ) : (
              <div className="td-hero-image-fallback">
                <i className="fa fa-cubes" aria-hidden />
              </div>
            )}
          </div>
          <div className="td-hero-main">
            <h1 className="td-hero-title">{product?.name || <em>Unregistered product</em>}</h1>
            <p className="td-hero-sub">Physical tag · {tag.tid}</p>
            <div className="td-hero-badges">
              <span className="td-badge td-badge-state" style={{ background: stateStyle.bg, color: stateStyle.color }}>
                <i className="fa fa-circle" style={{ fontSize: 7 }} aria-hidden /> {stateStyle.label}
              </span>
              {room && (
                <span className="td-badge td-badge-soft">
                  <i className="fa fa-cube" aria-hidden /> {room.roomTitle}
                </span>
              )}
              {warehouse && (
                <span className="td-badge td-badge-soft">
                  <i className="fa fa-building" aria-hidden /> {warehouse.name}
                </span>
              )}
              {location && (
                <span className="td-badge td-badge-soft">
                  <i className="fa fa-map-marker" aria-hidden /> {location.name}
                </span>
              )}
              <span className={`td-badge td-badge-expiry td-badge-expiry-${endTone}`}>
                <i className="fa fa-calendar" aria-hidden />
                {endDate
                  ? (tag.expired
                      ? `Expired ${Math.abs(daysRemaining)}d ago`
                      : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`)
                  : "No end date"}
              </span>
            </div>
          </div>
        </div>

        {/* Facts grid */}
        <div className="td-facts-grid">
          <Fact label="TID" value={<code>{tag.tid}</code>} />
          <Fact label="EPC" value={<code>{tag.epc || "—"}</code>} />
          <Fact label="Serial number" value={product?.serialNumber ? <code>{product.serialNumber}</code> : <span className="td-fact-muted">Not set</span>} />
          <Fact label="Price" value={product?.price != null ? `$${Number(product.price).toFixed(2)}` : "—"} />
          <Fact label="Default expiry" value={typeof product?.defaultExpiryDays === "number"
            ? `${product.defaultExpiryDays} day${product.defaultExpiryDays === 1 ? "" : "s"} (product default)`
            : "Not set on product"} />
          <Fact label="End date" value={endDate ? endDate.toLocaleDateString() : "Not set"} />
          <Fact label="RSSI" value={tag.rssi != null ? `${tag.rssi} dBm` : "—"} />
          <Fact label="Created" value={tag.createdAt ? new Date(tag.createdAt).toLocaleString() : "—"} />
          <Fact label="Updated" value={tag.updatedAt ? new Date(tag.updatedAt).toLocaleString() : "—"} />
        </div>

        {/* Edit cards — gated by role */}
        {canEdit && (
          <div className="td-edit-grid">
            {/* End date */}
            <div className="td-edit-card">
              <div className="td-edit-card-title">
                <i className="fa fa-calendar" aria-hidden /> End date
              </div>
              <p className="td-edit-help">
                Pick a date or use the quick offsets. Clearing sets it back to "no end date".
              </p>
              <div className="td-edit-row">
                <input
                  type="date"
                  className="td-date-input"
                  value={draftEndDate}
                  onChange={(e) => setDraftEndDate(e.target.value)}
                  disabled={savingField === "endDate"}
                />
                <div className="td-offset-group">
                  <button type="button" onClick={() => offsetEndDate(30, "days")}>+30 d</button>
                  <button type="button" onClick={() => offsetEndDate(6, "months")}>+6 mo</button>
                  <button type="button" onClick={() => offsetEndDate(1, "years")}>+1 y</button>
                  {draftEndDate && (
                    <button type="button" className="td-offset-clear" onClick={() => setDraftEndDate("")} title="Clear">
                      <i className="fa fa-eraser" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
              <div className="td-edit-footer">
                <button
                  type="button"
                  className="td-save-btn"
                  disabled={!endDirtyState || savingField === "endDate"}
                  onClick={saveEndDate}
                >
                  {savingField === "endDate"
                    ? <><i className="fa fa-spinner fa-spin" aria-hidden /> Saving…</>
                    : <><i className="fa fa-check" aria-hidden /> Save</>}
                </button>
              </div>
            </div>

            {/* State */}
            <div className="td-edit-card">
              <div className="td-edit-card-title">
                <i className="fa fa-flag" aria-hidden /> State
              </div>
              <p className="td-edit-help">
                The tag's current state. Server handles gate transitions (READY → ON_DELIVERY at a gate, etc.).
              </p>
              <div className="td-state-group">
                {["READY", "BLOCKED", "ON_DELIVERY"].map((s) => {
                  const style = STATE_STYLE[s];
                  const active = draftState === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`td-state-pill${active ? " is-active" : ""}`}
                      style={active
                        ? { background: style.bg, color: style.color, borderColor: style.color }
                        : undefined}
                      onClick={() => setDraftState(s)}
                      disabled={savingField === "state"}
                    >
                      {style.label}
                    </button>
                  );
                })}
              </div>
              <div className="td-edit-footer">
                <button
                  type="button"
                  className="td-save-btn"
                  disabled={!stateDirty || savingField === "state"}
                  onClick={saveState}
                >
                  {savingField === "state"
                    ? <><i className="fa fa-spinner fa-spin" aria-hidden /> Saving…</>
                    : <><i className="fa fa-check" aria-hidden /> Save</>}
                </button>
              </div>
            </div>

            {/* Room (move) */}
            {canMoveRoom && (
              <div className="td-edit-card">
                <div className="td-edit-card-title">
                  <i className="fa fa-exchange" aria-hidden /> Room
                </div>
                <p className="td-edit-help">
                  Moving between rooms creates a <strong>pending transfer</strong> (a manager must accept at the gate).
                  Setting to "Unassigned" applies immediately.
                </p>
                <div className="td-edit-row">
                  <div className="td-select-wrap">
                    <Dropdown
                      value={roomsLoading ? "" : String(draftRid)}
                      onChange={(v) => setDraftRid(v)}
                      disabled={savingField === "rid" || roomsLoading}
                      placeholder={roomsLoading ? "Loading rooms…" : "Select a room"}
                      iconLeft="fa-cube"
                      width="100%"
                      options={
                        roomsLoading
                          ? []
                          : [
                              { value: "0", label: "— Unassigned —" },
                              ...rooms.map((r) => ({
                                value: String(r.rid),
                                label: `#${r.rid} · ${r.roomTitle}`,
                              })),
                            ]
                      }
                    />
                    {roomsLoading && (
                      <i className="fa fa-spinner fa-spin td-select-spinner" aria-hidden />
                    )}
                  </div>
                </div>
                <div className="td-edit-footer">
                  <button
                    type="button"
                    className="td-save-btn"
                    disabled={!ridDirty || savingField === "rid"}
                    onClick={saveRid}
                  >
                    {savingField === "rid"
                      ? <><i className="fa fa-spinner fa-spin" aria-hidden /> Saving…</>
                      : <><i className="fa fa-check" aria-hidden /> Save</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent activity */}
        <div className="td-section">
          <div className="td-section-header">
            <h3><i className="fa fa-history" aria-hidden /> Recent activity</h3>
            <span className="td-section-hint">{data?.recentAudit?.length || 0} event(s)</span>
          </div>
          {(() => {
            const all = data?.recentAudit || [];
            if (all.length === 0) {
              return (
                <div className="empty-state">
                  <i className="fa fa-history empty-state-icon" />
                  <p>No recorded activity for this tag yet.</p>
                </div>
              );
            }
            const totalPages = Math.max(1, Math.ceil(all.length / ACTIVITY_PAGE_SIZE));
            const page = Math.min(activityPage, totalPages);
            const start = (page - 1) * ACTIVITY_PAGE_SIZE;
            const pageItems = all.slice(start, start + ACTIVITY_PAGE_SIZE);
            return (
              <>
                <ul className="td-activity-list">
                  {pageItems.map((evt) => {
                    const meta = classifyType(evt.type);
                    return (
                      <li key={evt._id} className="td-activity-item">
                        <span className={`td-activity-dot td-activity-dot-${meta.family}`}>
                          <i className={`fa ${meta.icon}`} aria-hidden />
                        </span>
                        <div className="td-activity-main">
                          <span className="td-activity-desc">{evt.description || evt.type}</span>
                          <span className="td-activity-meta">
                            <span>{evt.user || "SYSTEM"}{evt.role ? ` · ${evt.role}` : ""}</span>
                            <span>·</span>
                            <span title={new Date(evt.timestamp).toLocaleString()}>{formatWhen(evt.timestamp)}</span>
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {totalPages > 1 && (
                  <div className="td-activity-pager">
                    <button
                      type="button"
                      className="td-pager-btn"
                      disabled={page <= 1}
                      onClick={() => setActivityPage(page - 1)}
                      aria-label="Previous page"
                    >
                      <i className="fa fa-chevron-left" aria-hidden /> Prev
                    </button>
                    <span className="td-pager-info">
                      Page {page} of {totalPages} · showing {start + 1}–{Math.min(start + ACTIVITY_PAGE_SIZE, all.length)} of {all.length}
                    </span>
                    <button
                      type="button"
                      className="td-pager-btn"
                      disabled={page >= totalPages}
                      onClick={() => setActivityPage(page + 1)}
                      aria-label="Next page"
                    >
                      Next <i className="fa fa-chevron-right" aria-hidden />
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

const Fact = ({ label, value }) => (
  <div className="td-fact">
    <span className="td-fact-label">{label}</span>
    <span className="td-fact-value">{value}</span>
  </div>
);

export default TagDetails;
